'use client'

/**
 * SentryProvider — initializes the Sentry/GlitchTip browser SDK once the
 * platform boots and the DSN is available from configzen.
 *
 * `ObservabilityInit` fetches the DSN from /v1/observability/effective (admins only). If the
 * request fails or the DSN is empty, Sentry is not initialized (silent
 * graceful degradation — matches the T13 pattern for backend sources).
 *
 * Also wraps children in a Sentry ErrorBoundary so uncaught React errors
 * are reported to Sentry (FR-208).
 *
 * Ref: ASC-3655 / FR-201, FR-205, FR-208.
 */

import { useEffect } from 'react'
import { PLATFORM_SETTINGS } from '@platform/accesszen-contracts'
import { useCanAccess } from '@platform/accesszen-react'
import { initSentry, TracedErrorBoundary } from '@platform/observezen-react'
import { ErrorState } from '@platform/errorzen-react'

/**
 * Mount inside AccesszenProvider. The endpoint requires platform:settings admin, so the
 * fetch runs only for callers who hold it — it used to fire on every page load and 403
 * for every non-admin. Ref: ASC-11219 (F-35b)
 */
export function ObservabilityInit(): null {
  const canReadObservability = useCanAccess(PLATFORM_SETTINGS, 'admin')
  useEffect(() => {
    if (!canReadObservability) return
    const fetchAndInit = async () => {
      try {
        // eslint-disable-next-line no-restricted-syntax -- safe-fetch-ok: pre-existing raw fetch call, grandfathered under the ASC-6361 lint ramp — migrate to safeFetch in a follow-up
        const res = await fetch('/api/gateway/v1/observability/effective', {
          credentials: 'include',
        })
        if (!res.ok) return
        const json = (await res.json()) as Record<string, unknown>
        const settings = json.settings as Record<string, unknown> | undefined
        const dsn = settings?.sentry_dsn
        const environment =
          (settings?.deployment_environment as string | undefined) ?? 'development'

        // Sentry runtime tunables (configzen observezen.sentry.* → /effective).
        // Numbers govern feature volume without a redeploy. Replay defaults to 0
        // (off, GlitchTip-friendly); raise on a real Sentry DSN to enable it.
        // Ref: ASC-5052 (configzen tunables).
        const num = (v: unknown, fallback: number): number => (typeof v === 'number' ? v : fallback)

        // Sentry init (FR-205) — graceful no-op when DSN absent. initSentry comes
        // from the shared @platform/observezen-react lib (single source of truth)
        // so platform host + agents share one tagged, OTEL-aware init. Ref: ASC-5130.
        if (typeof dsn === 'string' && dsn && dsn !== '***') {
          initSentry({
            dsn,
            environment,
            // CI sets NEXT_PUBLIC_SENTRY_RELEASE to the deploy SHA (FR-206); the
            // platform reads it here so the lib stays framework-agnostic.
            release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
            sampleRate: num(settings?.fe_sample_rate, 1.0),
            tracesSampleRate: num(settings?.fe_traces_rate, 0.0),
            replaysSessionSampleRate: num(settings?.fe_replay_rate, 0.0),
            replaysOnErrorSampleRate: num(settings?.fe_replay_on_error_rate, 0.0),
          })
        }

        // NOTE: FE → SigNoz (initBrowserTracing / OtlpLogTransport) is intentionally
        // NOT wired. Architecture rule: SigNoz = backend, Sentry = frontend. FE
        // perf is owned by Sentry's browserTracingIntegration; the only FE↔SigNoz
        // link is the otel.trace_id tag on each Sentry event. Ref: ASC-5052 won't-do.
      } catch {
        // silent — observability is progressive enhancement; init failure is not fatal
      }
    }
    void fetchAndInit()
  }, [canReadObservability])

  return null
}

export function SentryProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <TracedErrorBoundary
      mfeName="platform"
      fallback={({ resetError }) => (
        <div className="flex h-screen items-center justify-center">
          <ErrorState
            title="Something went wrong"
            description="An unexpected error occurred. Please try refreshing the page."
            retryable
            onRetry={resetError}
          />
        </div>
      )}
    >
      {children as React.ReactElement}
    </TracedErrorBoundary>
  )
}
